import { useEffect, useRef, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { API, Auth } from "aws-amplify"
import { normalizeInvoicingPercent } from "components/CertHistory/CertHistoryRowContent/certHistoryRowContentUtils"
import {
  buildGroupedInvoiceConcept,
  buildPrefilledInvoiceConcepts,
  collectLeafParts,
  getPartSelectionKey,
  getSelectedInvoiceParts,
  getSelectedPartAmount,
} from "../utils/certificationsHistoryInvoiceDraft"

const useCertificationsHistoryInvoiceDraft = ({
  certHistoryTree,
  projectId,
  onInvoiceCreated,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isInvoicing, setIsInvoicing] = useState(false)
  const [isInvoiceDraftConfirmOpen, setIsInvoiceDraftConfirmOpen] =
    useState(false)
  const [selectedParts, setSelectedParts] = useState({})
  const [invoicingDrafts, setInvoicingDrafts] = useState({})
  const hasInitializedFromTreeRef = useRef(false)

  useEffect(() => {
    setInvoicingDrafts({})
    setSelectedParts({})
    setIsInvoiceDraftConfirmOpen(false)
    setIsInvoicing(false)
    hasInitializedFromTreeRef.current = false
  }, [projectId])

  useEffect(() => {
    if (!location.state?.invoiceCreated) return
    setInvoicingDrafts({})
    setSelectedParts({})
    setIsInvoiceDraftConfirmOpen(false)
    hasInitializedFromTreeRef.current = false
    onInvoiceCreated?.()
    navigate(location.pathname, { replace: true, state: {} })
  }, [location.state?.invoiceCreated])

  useEffect(() => {
    if (!location.state?.fromInvoiceDraft) return
    setInvoicingDrafts({})
    setSelectedParts({})
    setIsInvoiceDraftConfirmOpen(false)
    hasInitializedFromTreeRef.current = false
    navigate(location.pathname, { replace: true, state: {} })
  }, [location.state?.fromInvoiceDraft])

  useEffect(() => {
    if (hasInitializedFromTreeRef.current) return
    if (!certHistoryTree?.certs?.length) return

    const nextSelectedParts = {}
    const nextInvoicingDrafts = {}

    certHistoryTree.certs.forEach(cert => {
      ;(cert.chapters || []).forEach(chapter => {
        collectLeafParts(chapter).forEach(part => {
          const hasBackendValue =
            part?.invoiceAmountPending != null && part.invoiceAmountPending > 0
          if (!hasBackendValue) return

          const certificationId = part?.cert?.certification_id
          const partId = part?.partidaId
          if (!certificationId || !partId) return

          nextSelectedParts[getPartSelectionKey(part)] = true

          if (part.invoiceAmountPending != null) {
            nextInvoicingDrafts[certificationId] = {
              ...(nextInvoicingDrafts[certificationId] || {}),
              [partId]: {
                invoicing: Number(part.invoiceAmountPending),
              },
            }
          }
        })
      })
    })

    if (Object.keys(nextSelectedParts).length > 0) {
      setSelectedParts(nextSelectedParts)
      setInvoicingDrafts(nextInvoicingDrafts)
      setIsInvoicing(true)
    }

    hasInitializedFromTreeRef.current = true
  }, [certHistoryTree])

  const handleToggleSelectedPart = part => {
    const selectionKey = getPartSelectionKey(part)
    const isCurrentlySelected = selectedParts[selectionKey]

    if (isCurrentlySelected) {
      const certificationId = part?.cert?.certification_id
      const partId = part?.partidaId
      const previousAmount =
        invoicingDrafts?.[certificationId]?.[partId]?.invoicing ??
        part?.invoiceAmountPending ??
        0
      handlePersistInvoicingAmount(part, previousAmount, 0)
      setInvoicingDrafts(prev => ({
        ...prev,
        [certificationId]: {
          ...(prev[certificationId] || {}),
          [partId]: { invoicing: 0 },
        },
      }))
      setSelectedParts(prev => {
        const { [selectionKey]: _removed, ...rest } = prev
        return rest
      })
      return
    }

    setSelectedParts(prev => ({ ...prev, [selectionKey]: true }))
  }

  const handleToggleSelectedChapter = chapter => {
    const leafParts = collectLeafParts(chapter)
    if (leafParts.length === 0) return

    const leafPartSelectionKeys = leafParts.map(getPartSelectionKey)
    const isFullySelected = leafParts.every(
      part => selectedParts[getPartSelectionKey(part)],
    )

    if (isFullySelected) {
      leafParts.forEach(part => {
        const certificationId = part?.cert?.certification_id
        const partId = part?.partidaId
        if (!certificationId || !partId) return
        const previousAmount =
          invoicingDrafts?.[certificationId]?.[partId]?.invoicing ??
          part?.invoiceAmountPending ??
          0
        handlePersistInvoicingAmount(part, previousAmount, 0)
      })

      setSelectedParts(previousSelectedParts => {
        const nextSelectedParts = { ...previousSelectedParts }

        leafPartSelectionKeys.forEach(selectionKey => {
          delete nextSelectedParts[selectionKey]
        })

        return nextSelectedParts
      })

      setInvoicingDrafts(previousDrafts => {
        const nextDrafts = { ...previousDrafts }

        leafParts.forEach(part => {
          const certificationId = part?.cert?.certification_id
          const partId = part?.partidaId
          if (!certificationId || !partId) return

          nextDrafts[certificationId] = {
            ...(nextDrafts[certificationId] || {}),
            [partId]: { invoicing: 0 },
          }
        })

        return nextDrafts
      })

      return
    }

    setSelectedParts(previousSelectedParts => {
      const nextSelectedParts = { ...previousSelectedParts }

      leafPartSelectionKeys.forEach(selectionKey => {
        nextSelectedParts[selectionKey] = true
      })

      return nextSelectedParts
    })
  }

  const handleUpdateInvoicingDraft = (part, invoicing) => {
    const certificationId = part?.cert?.certification_id
    const partId = part?.partidaId
    if (!certificationId || !partId) return

    const nextInvoicing = Number(invoicing) || 0

    setInvoicingDrafts(previousDrafts => ({
      ...previousDrafts,
      [certificationId]: {
        ...(previousDrafts[certificationId] || {}),
        [partId]: {
          invoicing: nextInvoicing,
        },
      },
    }))
  }

  const handlePersistInvoicingAmount = async (
    part,
    previousAmount,
    nextAmount,
  ) => {
    const certificationId = part?.cert?.certification_id
    const partId = part?.partidaId
    if (!certificationId || !partId || !projectId) return

    const draftAmount =
      nextAmount !== undefined
        ? nextAmount
        : invoicingDrafts?.[certificationId]?.[partId]?.invoicing
    const invoiceAmount = Number(draftAmount ?? part?.invoiceAmountPending ?? 0)

    if (Number(previousAmount) === invoiceAmount) return

    try {
      const headers = {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
        "Access-Control-Allow-Origin": "*",
      }

      await API.patch(
        "CORE",
        `/cert/invoicing/${projectId}/${certificationId}/${partId}`,
        {
          headers,
          queryStringParameters: { invoice_amount: invoiceAmount },
        },
      )
    } catch (error) {
      console.error("Error persisting invoicing amount", error)
      setInvoicingDrafts(previousDrafts => ({
        ...previousDrafts,
        [certificationId]: {
          ...(previousDrafts[certificationId] || {}),
          [partId]: {
            invoicing: Number(previousAmount) || 0,
          },
        },
      }))
    }
  }

  const selectedInvoiceParts = getSelectedInvoiceParts(
    certHistoryTree,
    selectedParts,
  )

  const handleApplyPercentToSelected = percent => {
    if (selectedInvoiceParts.length === 0) return

    const normalizedPercent = Math.min(Math.max(Number(percent) || 0, 0), 100)

    setInvoicingDrafts(previousDrafts => {
      const nextDrafts = { ...previousDrafts }

      selectedInvoiceParts.forEach(part => {
        const certificationId = part?.cert?.certification_id
        const partId = part?.partidaId
        if (!certificationId || !partId) return

        const nextInvoicing = normalizeInvoicingPercent(part, normalizedPercent)

        nextDrafts[certificationId] = {
          ...(nextDrafts[certificationId] || {}),
          [partId]: {
            invoicing: nextInvoicing,
          },
        }
      })

      return nextDrafts
    })
  }

  const selectedPartsWithAmount = selectedInvoiceParts.filter(
    part => getSelectedPartAmount(part, invoicingDrafts) > 0,
  )
  const selectedPartsCount = selectedInvoiceParts.length
  const selectedInvoicingTotal = selectedPartsWithAmount.reduce(
    (sum, part) => sum + getSelectedPartAmount(part, invoicingDrafts),
    0,
  )

  const handleToggleInvoicing = () => {
    setIsInvoicing(prev => !prev)
  }

  const handleClearSelectedParts = () => {
    selectedInvoiceParts.forEach(part => {
      const certificationId = part?.cert?.certification_id
      const partId = part?.partidaId
      if (!certificationId || !partId) return
      const previousAmount =
        invoicingDrafts?.[certificationId]?.[partId]?.invoicing ??
        part?.invoiceAmountPending ??
        0
      handlePersistInvoicingAmount(part, previousAmount, 0)
    })

    setInvoicingDrafts(previousDrafts => {
      const nextDrafts = { ...previousDrafts }

      selectedInvoiceParts.forEach(part => {
        const certificationId = part?.cert?.certification_id
        const partId = part?.partidaId
        if (!certificationId || !partId) return

        nextDrafts[certificationId] = {
          ...(nextDrafts[certificationId] || {}),
          [partId]: { invoicing: 0 },
        }
      })

      return nextDrafts
    })
    setSelectedParts({})
    setIsInvoiceDraftConfirmOpen(false)
  }

  const handleOpenInvoiceDraftConfirm = () => {
    if (selectedPartsCount === 0) return
    setIsInvoiceDraftConfirmOpen(true)
  }

  const handleCloseInvoiceDraftConfirm = () => {
    setIsInvoiceDraftConfirmOpen(false)
  }

  const handleAcceptInvoiceDraft = (groupParts = false) => {
    if (selectedPartsWithAmount.length === 0 || !projectId) return

    const prefilledConcepts = groupParts
      ? buildGroupedInvoiceConcept(
          selectedPartsWithAmount,
          invoicingDrafts,
          selectedInvoicingTotal,
        )
      : buildPrefilledInvoiceConcepts(selectedPartsWithAmount, invoicingDrafts)

    setIsInvoiceDraftConfirmOpen(false)
    navigate(`/invoice/new/${projectId}`, {
      state: {
        prefilledConcepts,
        returnTo: `/certhistory/${projectId}`,
      },
    })
  }

  return {
    isInvoicing,
    handleToggleInvoicing,
    handleClearSelectedParts,
    isInvoiceDraftConfirmOpen,
    handleOpenInvoiceDraftConfirm,
    handleCloseInvoiceDraftConfirm,
    handleAcceptInvoiceDraft,
    selectedParts,
    invoicingDrafts,
    handleToggleSelectedPart,
    handleToggleSelectedChapter,
    handleUpdateInvoicingDraft,
    handlePersistInvoicingAmount,
    handleApplyPercentToSelected,
    selectedPartsCount,
    selectedInvoicingTotal,
  }
}

export default useCertificationsHistoryInvoiceDraft
